import { useCallback, useMemo } from 'react'
import { Variable } from '@mobileTypes'
import { STORAGE_KEY_MOBILE_GLOBAL_VARIABLES } from '@mobileConstants'

const getGlobalVariablesFromStorage = (): Variable[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_MOBILE_GLOBAL_VARIABLES)
    const raw = stored ? JSON.parse(stored) : []
    return Array.isArray(raw)
      ? raw.map((v: Variable) => ({ ...v, internalId: v.internalId ?? '' }))
      : []
  } catch (error) {
    console.error('Error loading global variables from storage:', error)
  }
  return []
}

const saveGlobalVariablesToStorage = (variables: Variable[]) => {
  try {
    localStorage.setItem(STORAGE_KEY_MOBILE_GLOBAL_VARIABLES, JSON.stringify(variables))
  } catch (error) {
    console.error('Error saving global variables to storage:', error)
  }
}

export const useMobileGlobalVariables = () => {
  const variables = useMemo(() => getGlobalVariablesFromStorage(), [])

  const createGlobalVariable = useCallback(
    (variable: Omit<Variable, 'createdAt' | 'updatedAt'>) => {
      const newVariable: Variable = {
        ...variable,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }
      const updated = [...variables, newVariable]
      saveGlobalVariablesToStorage(updated)
      return newVariable
    },
    [variables]
  )

  const updateGlobalVariable = useCallback(
    (internalId: string, variable: Partial<Variable>) => {
      const updated = variables.map(v =>
        v.internalId === internalId ? { ...v, ...variable, updatedAt: Date.now() } : v
      )
      saveGlobalVariablesToStorage(updated)
      return updated.find(v => v.internalId === internalId) || null
    },
    [variables]
  )

  const deleteGlobalVariable = useCallback(
    (internalId: string) => {
      const updated = variables.filter(v => v.internalId !== internalId)
      saveGlobalVariablesToStorage(updated)
    },
    [variables]
  )

  const getGlobalVariableByInternalId = useCallback(
    (internalId: string) => {
      return variables.find(v => v.internalId === internalId) || null
    },
    [variables]
  )

  const copyGlobalVariable = useCallback(
    (internalId: string, newInternalId: string) => {
      const variable = getGlobalVariableByInternalId(internalId)
      if (!variable) return null

      const copied: Variable = {
        ...variable,
        internalId: newInternalId,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }
      const updated = [...variables, copied]
      saveGlobalVariablesToStorage(updated)
      return copied
    },
    [variables, getGlobalVariableByInternalId]
  )

  return {
    variables,
    loading: false,
    createGlobalVariable,
    updateGlobalVariable,
    deleteGlobalVariable,
    getGlobalVariableByInternalId,
    copyGlobalVariable,
  }
}
